import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Wallet, RefreshCw, Trash2, Eye, EyeOff, TrendingUp, TrendingDown, AlertCircle, CheckCircle, Link2 } from 'lucide-react';
import AffiliateDisclosure from '@/components/AffiliateDisclosure';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { PortfolioAnalysis } from './PortfolioAnalysis';

interface PortfolioAsset {
  symbol: string;
  name: string;
  balance: number;
  value_aud: number;
  price_aud: number;
  change_24h?: number;
}

interface SwyftxBalance {
  assetId: number;
  code: string;
  name: string;
  availableBalance: string;
}

interface SwyftxAssetInfo {
  id: number;
  code: string;
  name: string;
  price?: {
    ask: string;
    bid: string;
  };
}

const STORAGE_KEY = 'swyftx_api_credentials';

export const PortfolioExposure = () => {
  const [apiKey, setApiKey] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [portfolio, setPortfolio] = useState<PortfolioAsset[]>([]);
  const [totalValue, setTotalValue] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<{
    balancesReceived: number;
    pricedAssets: number;
    droppedZeroValue: number;
    pricesAvailable: boolean;
  } | null>(null);
  const { toast } = useToast();

  // Load saved credentials on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { apiKey: savedKey, portfolio: savedPortfolio, lastUpdated: savedTime } = JSON.parse(saved);
        if (savedKey) {
          setApiKey(savedKey);
          setIsConnected(true);
          if (savedPortfolio) {
            setPortfolio(savedPortfolio);
            setTotalValue(savedPortfolio.reduce((sum: number, a: PortfolioAsset) => sum + a.value_aud, 0));
          }
          if (savedTime) {
            setLastUpdated(new Date(savedTime));
          }
        }
      } catch (e) {
        console.error('Failed to parse saved credentials:', e);
      }
    }
  }, []);

  const saveToStorage = (key: string, portfolioData: PortfolioAsset[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      apiKey: key,
      portfolio: portfolioData,
      lastUpdated: new Date().toISOString()
    }));
  };

  const testConnection = async () => {
    const trimmed = apiKey.trim();
    if (!trimmed) {
      setError('Please enter your Swyftx API key');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('proxy-swyftx-portfolio', {
        body: { apiKey: trimmed, testOnly: true },
      });
      if (fnError) throw new Error(fnError.message || 'Edge function error');
      if (data?.error) {
        const detail = data.swyftxStatus ? ` (Swyftx ${data.swyftxStatus})` : '';
        throw new Error(`${data.error}${detail}`);
      }
      toast({ title: 'Connection OK', description: 'Swyftx accepted your API key.' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Test failed';
      setError(message);
      toast({ title: 'Test failed', description: message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPortfolio = async () => {
    const trimmed = apiKey.trim();
    if (!trimmed) {
      setError('Please enter your Swyftx API key');
      return;
    }
    if (trimmed !== apiKey) setApiKey(trimmed);

    setIsLoading(true);
    setError(null);

    try {
      // Call backend proxy to avoid CORS issues
      const { data, error: fnError } = await supabase.functions.invoke('proxy-swyftx-portfolio', {
        body: { apiKey: trimmed }
      });

      if (fnError) {
        throw new Error(fnError.message || 'Failed to connect to Swyftx');
      }

      if (data?.error) {
        const detail = data.swyftxStatus ? ` (Swyftx ${data.swyftxStatus})` : '';
        throw new Error(`${data.error}${detail}`);
      }

      const portfolioAssets: PortfolioAsset[] = (data.holdings || []).map((h: any) => ({
        symbol: h.symbol,
        name: h.name,
        balance: h.balance,
        value_aud: h.value_aud,
        price_aud: h.price_aud,
        change_24h: undefined,
      }));

      const total = typeof data.totalAud === 'number'
        ? data.totalAud
        : portfolioAssets.reduce((sum, a) => sum + a.value_aud, 0);

      setPortfolio(portfolioAssets);
      setTotalValue(total);
      setIsConnected(true);
      setLastUpdated(new Date());
      saveToStorage(trimmed, portfolioAssets);

      const pricesAvailable = data.pricesAvailable !== false;
      const balancesReceived = data?.diagnostics?.balancesReceived ?? portfolioAssets.length;
      setDiagnostics({
        balancesReceived,
        pricedAssets: data?.diagnostics?.pricedAssets ?? 0,
        droppedZeroValue: data?.diagnostics?.droppedZeroValue ?? 0,
        pricesAvailable,
      });

      if (portfolioAssets.length === 0) {
        if (balancesReceived === 0) {
          toast({
            title: 'Connected — no balances',
            description: 'Swyftx returned no holdings on this account. Deposit or trade first, then refresh.',
          });
        } else if (!pricesAvailable) {
          toast({
            title: 'Connected — prices unavailable',
            description: 'Swyftx pricing is temporarily unavailable. Try Refresh in a minute.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Connected',
            description: 'No priceable holdings found.',
          });
        }
      } else {
        toast({
          title: 'Portfolio synced',
          description: `Found ${portfolioAssets.length} assets worth A$${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}${pricesAvailable ? '' : ' (prices unavailable)'}`,
        });
      }

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch portfolio';
      setError(message);
      toast({
        title: 'Connection failed',
        description: message,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const disconnect = () => {
    localStorage.removeItem(STORAGE_KEY);
    setApiKey('');
    setIsConnected(false);
    setPortfolio([]);
    setTotalValue(0);
    setLastUpdated(null);
    setError(null);
    setDiagnostics(null);
    toast({
      title: 'Disconnected',
      description: 'Your API credentials have been removed'
    });
  };

  const formatValue = (value: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const getPortfolioWeight = (value: number) => {
    if (totalValue === 0) return 0;
    return (value / totalValue) * 100;
  };

  return (
    <div className="space-y-4">
    <Card className="gradient-card border-border/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              Your Portfolio Exposure
            </CardTitle>
            <CardDescription>
              {isConnected 
                ? 'Your current holdings synced from Swyftx' 
                : 'Connect your Swyftx account (Read-Only API)'}
            </CardDescription>
          </div>
          {isConnected && lastUpdated && (
            <div className="text-xs text-muted-foreground">
              Updated {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!isConnected ? (
          <div className="space-y-4">
            <Alert>
              <Link2 className="h-4 w-4" />
              <AlertDescription>
                <strong>Read-Only Access:</strong> Only enter an API key with <strong>Read</strong> permissions. 
                Your key is stored locally in your browser and never sent to our servers.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="apiKey">Swyftx API Key</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="apiKey"
                    type={showApiKey ? 'text' : 'password'}
                    placeholder="Enter your read-only API key"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <Button onClick={fetchPortfolio} disabled={isLoading}>
                  {isLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Connect'}
                </Button>
                <Button variant="outline" onClick={testConnection} disabled={isLoading}>
                  Test
                </Button>
              </div>
              <AffiliateDisclosure partners="Swyftx" />
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer text-primary underline">
                  How to create a working read-only key
                </summary>
                <ol className="list-decimal pl-5 mt-2 space-y-1">
                  <li>Open <a href="https://trade.swyftx.com/profile/api" target="_blank" rel="noopener noreferrer" className="text-primary underline">Swyftx → Profile → API</a>.</li>
                  <li>Click <strong>Create API Key</strong>, name it <em>Lovable Read-Only</em>.</li>
                  <li>Set scope to <strong>Read-only</strong> (uncheck Trading & Withdraw).</li>
                  <li><strong>Leave the IP Whitelist empty</strong> — our backend uses rotating IPs, so any whitelist will block it.</li>
                  <li>Copy the <strong>API Key</strong> value (the long string), <em>not</em> the Access Token.</li>
                  <li>Paste it above and click <strong>Test</strong>, then <strong>Connect</strong>.</li>
                </ol>
              </details>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Total Value */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-primary/10 border border-primary/20">
              <div>
                <div className="text-sm text-muted-foreground">Total Portfolio Value</div>
                <div className="text-2xl font-bold text-foreground">{formatValue(totalValue)}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchPortfolio} disabled={isLoading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button variant="ghost" size="sm" onClick={disconnect}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Diagnostics */}
            {diagnostics && (
              <div className="rounded-lg border border-border/50 bg-muted/30 p-3">
                <div className="text-xs font-semibold text-foreground mb-2">Sync diagnostics</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div>
                    <div className="text-muted-foreground">Balances received</div>
                    <div className="font-semibold text-foreground">{diagnostics.balancesReceived}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Priced assets</div>
                    <div className="font-semibold text-foreground">{diagnostics.pricedAssets.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Dropped (zero value)</div>
                    <div className="font-semibold text-foreground">{diagnostics.droppedZeroValue}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Prices available</div>
                    <Badge variant={diagnostics.pricesAvailable ? 'default' : 'destructive'} className="text-[10px] mt-0.5">
                      {diagnostics.pricesAvailable ? (
                        <><CheckCircle className="h-3 w-3 mr-1" /> Yes</>
                      ) : (
                        <><AlertCircle className="h-3 w-3 mr-1" /> No</>
                      )}
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            {/* Holdings */}
            {portfolio.length > 0 ? (
              <div className="space-y-2">
                <div className="text-sm font-semibold text-foreground mb-3">Holdings</div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {portfolio.map((asset) => (
                    <div 
                      key={asset.symbol}
                      className="flex items-center justify-between p-3 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-xs font-bold">{asset.symbol.slice(0, 3)}</span>
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">{asset.symbol}</div>
                          <div className="text-xs text-muted-foreground">{asset.name}</div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="font-semibold text-foreground">{formatValue(asset.value_aud)}</div>
                        <div className="text-xs text-muted-foreground">
                          {asset.balance.toLocaleString(undefined, { maximumFractionDigits: 4 })} {asset.symbol}
                        </div>
                      </div>

                      <div className="min-w-[60px] text-right">
                        <Badge variant="outline" className="text-xs">
                          {getPortfolioWeight(asset.value_aud).toFixed(1)}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Wallet className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No holdings found in your account</p>
              </div>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </CardContent>
    </Card>

    {isConnected && portfolio.length > 0 && (
      <PortfolioAnalysis portfolio={portfolio} totalValue={totalValue} />
    )}
    </div>
  );
};
